'use client';

import type { ReactNode } from 'react';
import { KBareButton } from './button';
import { cx } from './cx';

/**
 * СЕТКА МЕСЯЦА — ОДНА НА ВЕСЬ ВЕБ (ТЗ WEB-CAUSES М65, владелец 06.10.2026: «календари открываются
 * очень некрасивые… в десктоп-версии какие-то более красивые»). Раньше календарь жил своей разметкой
 * `.dr-*` в cabinet.css (попап под кнопкой со своими радиусами и цветами) и второй копией для окна
 * даты рассылки (R04). Теперь сетку рисует этот компонент, вид — `.k-cal*` в kit.css ролями палитры:
 * выбранный день — заливка `--accent`, сегодня — обводка `--accent-stroke`, промежуток — `--accent-soft`.
 * Окно (`KDialog`), пресеты и кнопки — снаружи; подписи (месяц, дни недели) даёт вызывающий на
 * языке кабинета. `size="lg"` — крупная сетка окна даты рассылки по кадру iOS R04 (клетка 44).
 */
export interface KCalendarDay {
  on?: boolean;
  mid?: boolean;
  disabled?: boolean;
}

type Props = {
  year: number;
  /** 0–11. */
  month: number;
  /** Первый день недели локали: 0 — воскресенье, 1 — понедельник. */
  firstDay: number;
  /** Заголовок месяца («Октябрь 2026 г.»). */
  title: ReactNode;
  /** Короткие дни недели в порядке колонок (с firstDay). */
  weekdays: string[];
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onPick: (day: Date) => void;
  day?: (d: Date) => KCalendarDay;
  /** Всегда шесть недель — высота окна не прыгает при листании (ТЗ ANIM-FIX О2). */
  sixRows?: boolean;
  /** Розовый «›» после месяца — знак родного календаря (кадр iOS R04, ТЗ WEB-CAUSES О11). */
  more?: boolean;
  size?: 'md' | 'lg';
  className?: string;
};

function Chevron({ back }: { back?: boolean }) {
  return (
    <svg width={12} height={20} viewBox="0 0 12 20" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={back ? 'M9.5 2 1.8 10l7.7 8' : 'M2.5 2l7.7 8-7.7 8'} />
    </svg>
  );
}

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function KCalendar({
  year,
  month,
  firstDay,
  title,
  weekdays,
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
  onPick,
  day,
  sixRows = false,
  more = false,
  size = 'md',
  className,
}: Props) {
  const lead = (new Date(year, month, 1).getDay() - firstDay + 7) % 7;
  const cells: (Date | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  const daysIn = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysIn; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  if (sixRows) while (cells.length < 42) cells.push(null);
  const today = new Date();
  return (
    <div className={cx('k-cal', size === 'lg' && 'k-cal--lg', className)}>
      <div className="k-cal__head">
        <b className="k-cal__title">
          {title}
          {more ? (
            <svg className="k-cal__more" width={8} height={13} viewBox="0 0 8 13" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M1.6 1.5 6.4 6.5l-4.8 5" />
            </svg>
          ) : null}
        </b>
        <span className="k-cal__nav">
          <KBareButton className="k-cal__arr" onClick={onPrev} aria-label={prevLabel}>
            <Chevron back />
          </KBareButton>
          <KBareButton className="k-cal__arr" onClick={onNext} aria-label={nextLabel}>
            <Chevron />
          </KBareButton>
        </span>
      </div>
      <div className="k-cal__grid">
        {weekdays.map((w) => (
          <span key={w} className="k-cal__wd">
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={i} className="k-cal__pad" />;
          const st = day?.(d) ?? {};
          return (
            <KBareButton
              key={i}
              className={cx('k-cal__day', st.on && 'is-on', st.mid && 'is-mid', sameDay(d, today) && 'is-today')}
              disabled={st.disabled}
              aria-pressed={st.on ? true : undefined}
              onClick={() => onPick(d)}
            >
              {d.getDate()}
            </KBareButton>
          );
        })}
      </div>
    </div>
  );
}
